
import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { ratingService } from '../../services/ratingService';

interface ArticleRatingProps {
  articleId: string;
  initialAverageRating?: number; // From ContentPage
  initialRatingCount?: number; // From ContentPage
  onRatingSubmit?: (rating: number) => void;
  className?: string;
}

const ArticleRating: React.FC<ArticleRatingProps> = ({
  articleId,
  initialAverageRating,
  initialRatingCount = 0,
  onRatingSubmit,
  className = '',
}) => {
  const { state } = useAuth();
  const [hoveredRating, setHoveredRating] = useState<number | null>(null);
  const [selectedRating, setSelectedRating] = useState<number | null>(null);
  const [averageRating, setAverageRating] = useState<number | null>(initialAverageRating ?? null);
  const [ratingCount, setRatingCount] = useState<number>(initialRatingCount);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingRating, setIsLoadingRating] = useState(!initialAverageRating);

  // Fetch rating data on mount (always fetch fresh from API)
  useEffect(() => {
    const fetchRatingData = async () => {
      try {
        setIsLoadingRating(true);
        // Reset state immediately on articleId change to avoid stale data
        setSelectedRating(null);
        setAverageRating(initialAverageRating ?? null);
        setRatingCount(initialRatingCount);

        // Always fetch fresh average from ratings API
        const avg = await ratingService.getAverage(articleId);
        setAverageRating(avg.average);
        setRatingCount(avg.count);

        // Fetch user's rating if authenticated
        if (state.isAuthenticated) {
          const userRating = await ratingService.getUserRating(articleId);
          if (userRating) {
            setSelectedRating(userRating.rating);
          }
        }
      } catch (error) {
        console.error('Failed to fetch rating data:', error);
      } finally {
        setIsLoadingRating(false);
      }
    };

    fetchRatingData();
  }, [articleId, state.isAuthenticated]);

  const handleRatingClick = async (rating: number) => {
    if (!state.isAuthenticated || isLoading) {
      return;
    }

    try {
      setIsLoading(true);

      // Save rating to backend
      await ratingService.createOrUpdate({
        contentPageId: articleId,
        rating,
      });

      setSelectedRating(rating);

      // Refresh average rating (ContentPage will be updated by backend, but we refresh here for immediate UI update)
      try {
        const avg = await ratingService.getAverage(articleId);
        setAverageRating(avg.average);
        setRatingCount(avg.count);
      } catch (error) {
        console.error('Failed to refresh average rating:', error);
        // Don't fail - rating is saved, average will be correct on next page load
      }

      if (onRatingSubmit) {
        onRatingSubmit(rating);
      }
    } catch (error) {
      console.error('Failed to save rating:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const getRatingStyle = (rating: number) => {
    const activeRating = hoveredRating || selectedRating || 0;
    const isActive = rating <= activeRating;

    if (isActive) {
      return 'is-active';
    }
    return 'is-inactive';
  };

  if (isLoadingRating) {
    return (
      <div className={`article-rating border-t border-gray-200 dark:border-gray-700 pt-8 mt-8 ${className}`}>
        <div className="animate-pulse">
          <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded w-64 mb-4"></div>
          <div className="flex gap-2">
            {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((i) => (
              <div key={i} className="w-10 h-10 bg-gray-200 dark:bg-gray-700 rounded-lg"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`article-rating border-t border-gray-200 dark:border-gray-700 pt-8 mt-8 ${className}`}>
      <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4">
        How helpful was this article?
      </h3>

      {averageRating !== null && ratingCount > 0 && (
        <div className="mb-4 text-sm text-gray-600 dark:text-gray-400">
          Average rating: <span className="font-semibold">{averageRating.toFixed(1)}</span> / 10
          ({ratingCount} {ratingCount === 1 ? 'rating' : 'ratings'})
        </div>
      )}

      {!state.isAuthenticated ? (
        <div className="flex flex-col gap-4">
          <div className="flex items-center gap-2 flex-wrap">
            {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((rating) => (
              <button
                key={rating}
                disabled
                className="article-rating__value is-disabled w-10 h-10 rounded-lg border-2 border-gray-300 dark:border-gray-600 bg-gray-100 dark:bg-gray-800 text-gray-400 dark:text-gray-600 cursor-not-allowed font-medium"
              >
                {rating}
              </button>
            ))}
          </div>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Please sign in to rate this article
          </p>
        </div>
      ) : (
        <div className="flex flex-col gap-4">
          <div className="flex items-center gap-2 flex-wrap">
            {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((rating) => (
              <button
                key={rating}
                onClick={() => handleRatingClick(rating)}
                onMouseEnter={() => setHoveredRating(rating)}
                onMouseLeave={() => setHoveredRating(null)}
                disabled={isLoading}
                className={`article-rating__value w-10 h-10 rounded-lg border-2 font-medium transition-all duration-200 ${getRatingStyle(rating)} ${isLoading ? 'opacity-50 cursor-not-allowed' : ''
                  }`}
                aria-label={`Rate ${rating} out of 10`}
              >
                {rating}
              </button>
            ))}
          </div>
          {selectedRating && (
            <p className="text-sm text-gray-600 dark:text-gray-400">
              You rated this article {selectedRating}/10
            </p>
          )}
          {isLoading && (
            <p className="text-xs text-gray-600 dark:text-gray-400">Saving rating...</p>
          )}
        </div>
      )}
    </div>
  );
};

export default ArticleRating;
